import { LookupType } from '@finxone-platform/shared/sys-config-types';
import {
  APP_LOOKUP_TYPES,
  AppAddressSuggestion,
  AppApiOperations,
  AppCompanyAddress,
  AppCompanyDetails,
  AppCompanySummary,
  AppLookupOption,
  AppPage,
  AppResolvedAddress,
} from '@finxone-platform/shared/app-sdk';
import { lastValueFrom } from 'rxjs';
import { CompaniesHouseService } from '../../../../lib/services/companies-house-service/companies-house.service';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import { DataSourceService } from '../../../../lib/services/data-source-service/data-source.service';

const LOOKUP_OPERATIONS = new Set([
  'companies.search',
  'companies.get',
  'addresses.suggest',
  'addresses.resolve',
  'lookups.list',
]);

/**
 * The lists `lookups.list` serves. `LookupType` also declares `support-chat-subjects`,
 * `companies-house-directors` and `default`; the first two are platform-internal surfaces
 * and `default` is not a list, so none of the three is in the contract.
 */
const WIDGET_LOOKUP_TYPES = new Set<string>(APP_LOOKUP_TYPES);

function requiredText(operation: string, field: string, value: unknown, maxLength: number): string {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) throw new Error(`${operation} requires ${field}`);
  if (text.length > maxLength) {
    throw new Error(`${operation} ${field} must be at most ${maxLength} characters`);
  }
  return text;
}

function companyAddress(address: AppCompanyAddress | undefined): AppCompanyAddress | undefined {
  return address ? { ...address } : undefined;
}

export function buildCustomWidgetLookupOperations(
  companiesHouseService: CompaniesHouseService,
  metadataService: MetadataService,
  // `lookups.list` only. Optional for the same reason the api bridge's other optional
  // services are: a host that wires no data source fails the operation with a message
  // saying so rather than answering an empty list, which a select cannot tell from a
  // genuinely empty one.
  dataSourceService?: Pick<DataSourceService, 'getDataSourceByType'>,
) {
  async function listLookups(
    payload: Partial<AppApiOperations['lookups.list']['payload']>,
  ): Promise<AppLookupOption[]> {
    if (!dataSourceService) {
      throw new Error('lookups.list is not available: this host has no data source service wired');
    }
    const type = String(payload?.type ?? '');
    if (!WIDGET_LOOKUP_TYPES.has(type)) throw new Error(`lookups.list: unknown type '${type}'`);
    const options = await lastValueFrom(dataSourceService.getDataSourceByType(type as LookupType));
    return (options ?? []).map((option) => ({
      value: String(option.value),
      label: String(option.label),
      ...(option.metadata && typeof option.metadata === 'object' && !Array.isArray(option.metadata)
        ? { metadata: { ...option.metadata } }
        : {}),
    }));
  }

  async function searchCompanies(
    payload: AppApiOperations['companies.search']['payload'],
  ): Promise<AppPage<AppCompanySummary>> {
    const companyName = typeof payload.companyName === 'string' ? payload.companyName.trim() : '';
    const location = typeof payload.location === 'string' ? payload.location.trim() : '';
    if (!companyName && !location) throw new Error('companies.search requires companyName or location');
    if (companyName.length > 200 || location.length > 200) {
      throw new Error('companies.search criteria must be at most 200 characters');
    }
    const page = Math.max(1, Math.floor(Number(payload.page ?? 1)) || 1);
    const size = Math.min(20, Math.max(1, Math.floor(Number(payload.size ?? 10)) || 10));
    const response = await lastValueFrom(companiesHouseService.searchCompanies({
      companyName: companyName || undefined,
      location: location || undefined,
      page,
      limit: size,
    }));
    return {
      items: (response.result ?? []).map((company) => ({
        companyName: company.companyName,
        companyNumber: company.companyNumber,
        companyStatus: company.companyStatus,
        type: company.type,
        dateOfCreation: company.dateOfCreation,
        registeredOfficeAddress: companyAddress(company.registeredOfficeAddress),
      })),
      page: response.page,
      totalItems: response.totalRecords,
    };
  }

  async function getCompany(
    payload: Partial<AppApiOperations['companies.get']['payload']>,
  ): Promise<AppCompanyDetails> {
    const companyNumber = requiredText('companies.get', 'companyNumber', payload.companyNumber, 32);
    const company = await lastValueFrom(companiesHouseService.checkCompanyNumber(companyNumber));
    return {
      companyName: company.companyName,
      companyNumber: company.companyNumber,
      companyStatus: company.companyStatus,
      dateOfCreation: company.dateOfCreation,
      jurisdiction: company.jurisdiction,
      type: company.type,
      undeliverableRegisteredOfficeAddress: company.undeliverableRegisteredOfficeAddress,
      registeredOfficeIsInDispute: company.registeredOfficeIsInDispute,
      registeredOfficeAddress: companyAddress(company.registeredOfficeAddress),
      serviceAddress: companyAddress(company.serviceAddress),
      officers: company.officers ? {
        activeCount: company.officers.activeCount,
        resignedCount: company.officers.resignedCount,
        totalResults: company.officers.totalResults,
        items: company.officers.items.map((officer) => ({
          name: officer.name,
          firstName: officer.firstName,
          lastName: officer.lastName,
          middleName: officer.middleName,
          officerRole: officer.officerRole,
          appointedOn: officer.appointedOn,
          resignedOn: officer.resignedOn,
          nationality: officer.nationality,
          occupation: officer.occupation,
          address: companyAddress(officer.address),
          dateOfBirth: officer.dateOfBirth?.month !== undefined && officer.dateOfBirth.year !== undefined
            ? { month: officer.dateOfBirth.month, year: officer.dateOfBirth.year }
            : undefined,
        })),
      } : undefined,
    };
  }

  async function suggestAddresses(
    payload: Partial<AppApiOperations['addresses.suggest']['payload']>,
  ): Promise<AppAddressSuggestion[]> {
    const query = requiredText('addresses.suggest', 'query', payload.query, 200);
    if (query.length < 3) throw new Error('addresses.suggest query must be at least 3 characters');
    const suggestions = await lastValueFrom(metadataService.autosuggestAddress(query));
    return (suggestions ?? []).slice(0, 6).map((suggestion) => ({
      id: String(suggestion.id),
      label: String(suggestion.address),
    }));
  }

  async function resolveAddress(
    payload: Partial<AppApiOperations['addresses.resolve']['payload']>,
  ): Promise<AppResolvedAddress> {
    const id = requiredText('addresses.resolve', 'id', payload.id, 512);
    const address = await lastValueFrom(metadataService.getAddressById(id));
    return {
      postcode: address.postcode,
      latitude: address.latitude,
      longitude: address.longitude,
      formattedAddress: Array.isArray(address.formatted_address)
        ? address.formatted_address.map(String).filter(Boolean)
        : [],
      district: address.district,
      county: address.county,
      country: address.country,
      city: address.city,
      state: address.state,
      street: address.street,
      propertyName: address.property_name,
      propertyNumber: address.property_number,
      line1: address.line_1,
      line2: address.line_2,
      line3: address.line_3,
      line4: address.line_4,
      flatName: address.flat_name,
    };
  }

  return {
    handles: (operation: string) => LOOKUP_OPERATIONS.has(operation),
    call: async (operation: string, payload: Record<string, unknown>) => {
      switch (operation) {
        case 'companies.search':
          return searchCompanies(payload as AppApiOperations['companies.search']['payload']);
        case 'companies.get':
          return getCompany(payload as Partial<AppApiOperations['companies.get']['payload']>);
        case 'addresses.suggest':
          return suggestAddresses(payload as Partial<AppApiOperations['addresses.suggest']['payload']>);
        case 'addresses.resolve':
          return resolveAddress(payload as Partial<AppApiOperations['addresses.resolve']['payload']>);
        case 'lookups.list':
          return listLookups(payload as Partial<AppApiOperations['lookups.list']['payload']>);
        default:
          throw new Error(`unknown-operation: ${operation}`);
      }
    },
  };
}
